import React, { useEffect, useMemo, useState } from "react";

import {
  getDashboardDeviceTypeLabel,
  normalizeBatteryValue,
  // 키와 이벤트 이름은 utils 한 곳에서 가져온다.
  // 여기에 따로 적어두는 바람에 표시 설정 쪽과 어긋나 설정이 반영되지 않았다.
  PRIORITY_PREVIEW_SELECTED_LIST_KEY,
  PRIORITY_PREVIEW_LIST_CHANGED_EVENT,
} from "../../utils/dashboardUtils";
import { getOwnerLine } from "../../utils/ownerLine";
import useCameraStatus from "../../hooks/useCameraStatus";

const MAX_PREVIEW_CAMERA_COUNT = 4;

function safeReadSelectedList() {
  try {
    const raw = localStorage.getItem(PRIORITY_PREVIEW_SELECTED_LIST_KEY);
    const parsed = JSON.parse(raw || "[]");

    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, MAX_PREVIEW_CAMERA_COUNT);
  } catch {
    return [];
  }
}

function getCameraKey(cam, idx) {
  return String(
    cam?.camIdx ??
      cam?.id ??
      cam?.camNo ??
      cam?.cameraId ??
      cam?.streamKey ??
      idx
  ).trim();
}

function getStatusKeyCandidates(cam, idx) {
  return [
    cam?.camIdx,
    cam?.id,
    cam?.camNo,
    cam?.cameraId,
    cam?.streamKey,
    idx,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
}

export default function LivePreview({ cameras = [] }) {
  // 카메라 상태는 공유 스토어에서 받는다(이 컴포넌트가 직접 폴링하지 않는다).
  const { statusMap, loadedOnce } = useCameraStatus();
  const statusLoading = !loadedOnce;

  const [selectedDisplayKeys, setSelectedDisplayKeys] = useState(() =>
    safeReadSelectedList()
  );

  useEffect(() => {
    const syncSelectedList = () => {
      setSelectedDisplayKeys(safeReadSelectedList());
    };

    // storage 이벤트는 '다른 탭'에서만 발생한다. 설정을 바꾼 그 탭에서도 바로
    // 반영되려면 저장한 쪽이 쏘는 커스텀 이벤트를 같이 들어야 한다.
    window.addEventListener("storage", syncSelectedList);
    window.addEventListener(PRIORITY_PREVIEW_LIST_CHANGED_EVENT, syncSelectedList);

    return () => {
      window.removeEventListener("storage", syncSelectedList);
      window.removeEventListener(
        PRIORITY_PREVIEW_LIST_CHANGED_EVENT,
        syncSelectedList
      );
    };
  }, []);

  /*
    예전에는 여기서 /api/my-cameras/status 를 5초마다 직접 불렀다.
    App.js·PriorityPreviewCard·DevicesPage 도 각자 같은 API를 불러서
    한 탭에서 같은 요청이 네 번씩 나갔다.
    이제 cameraStatusStore 한 곳이 폴링하고 화면들은 구독만 한다.
  */

  const previewItems = useMemo(() => {
    const list = Array.isArray(cameras) ? cameras : [];

    const normalizedList = list.map((cam, idx) => {
      const key = getCameraKey(cam, idx);

      const statusCandidates = getStatusKeyCandidates(cam, idx);
      let meta = {};

      for (const statusKey of statusCandidates) {
        if (statusMap[statusKey]) {
          meta = statusMap[statusKey] || {};
          break;
        }
      }

      const hasStatus = !!meta?.liveStatus;
      const liveStatus = String(meta?.liveStatus || "UNKNOWN").toUpperCase();
      const isOnline = liveStatus === "ONLINE";
      const isUnknown = !hasStatus || liveStatus === "UNKNOWN";

      return {
        key,
        camNo: cam?.camNo || idx + 1,
        name:
          cam?.name ||
          cam?.camName ||
          cam?.camTitle ||
          `CAM${cam?.camNo || idx + 1}`,
        camLabel: `CAM ${cam?.camNo || idx + 1}`,
        deviceType: getDashboardDeviceTypeLabel(cam),
        isOnline,
        isUnknown,
        liveStatus,
        battery: normalizeBatteryValue(meta?.battery),
        workerName: meta?.workerName || "",
        // 작업자 이름과 항상 같이 표시되는 값들(지점/팀/차량번호)
        vehicleNo: meta?.vehicleNo || "",
        teamName: meta?.teamName || "",
        branchName: meta?.branchName || "",
      };
    });

    // «송출 중»을 앞으로 정렬해서 채운다(2026-10-07).
    //   숨기지 않는 이유: 전부 꺼져 있을 때 카드가 텅 비어 고장처럼 보인다.
    //   정렬이면 ①전부 꺼짐 = 예전과 같은 모습 ②켜지는 즉시 맨 앞으로 ③빈 칸 안 생김.
    //   같은 온라인/오프라인끼리는 «우선표시» 지정 순서를 먼저 따른다.
    const priorityRank = new Map();
    selectedDisplayKeys.forEach((key, idx) => {
      if (!priorityRank.has(key)) priorityRank.set(key, idx);
    });

    const rankOf = (item) =>
      priorityRank.has(item.key) ? priorityRank.get(item.key) : Infinity;

    const sorted = [...normalizedList].sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;

      const ra = rankOf(a);
      const rb = rankOf(b);
      if (ra !== rb) return ra - rb;

      return 0;
    });

    return sorted.slice(0, MAX_PREVIEW_CAMERA_COUNT);
  }, [cameras, statusMap, selectedDisplayKeys]);

  const totalCameraCount = Array.isArray(cameras) ? cameras.length : 0;

  if (!previewItems.length) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center rounded-[18px] border border-blue-100 bg-white/80 text-sm text-gray-500 dark:border-blue-500/10 dark:bg-black/10 dark:text-gray-400">
        {statusLoading
          ? "카메라 상태 확인 중..."
          : totalCameraCount > 0
          ? "표시할 카메라가 없습니다."
          : "등록된 카메라가 없습니다."}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 max-h-full flex-col overflow-hidden">
      <div className="grid h-full min-h-0 grid-cols-2 gap-2 overflow-hidden">
        {previewItems.map((item) => (
          <div
            key={item.key}
            className="flex min-h-0 flex-col overflow-hidden rounded-[12px] border border-blue-100 bg-white/85 p-[7px] dark:border-blue-500/10 dark:bg-black/10"
          >
            <div className="flex min-h-0 items-start justify-between gap-1.5">
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1">
                  <div className="truncate text-[13px] font-bold leading-[1.1] text-gray-900 dark:text-white">
                    {item.name}
                  </div>

                  <div className="shrink-0 rounded-full bg-blue-50 px-1.5 py-[1px] text-[10px] font-semibold leading-none text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                    {item.deviceType}
                  </div>
                </div>

                <div className="mt-[2px] truncate text-[11px] leading-none text-gray-500 dark:text-gray-400">
                  {getOwnerLine(item) ||
                    `${item.camLabel}${
                      item.workerName ? ` / ${item.workerName}` : ""
                    }`}
                </div>
              </div>

              <span
                className={`mt-[2px] inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${
                  item.isOnline
                    ? "bg-emerald-500"
                    : item.isUnknown
                    ? "bg-slate-400"
                    : "bg-red-500"
                }`}
              />
            </div>

            <div className="mt-1.5 grid grid-cols-2 gap-1">
              <div className="min-w-0 rounded-md bg-slate-50 px-1.5 py-1 dark:bg-white/5">
                <div className="text-[10px] font-medium leading-none text-gray-400 dark:text-gray-500">
                  상태
                </div>

                <div
                  className={`mt-1 truncate text-[11px] font-semibold leading-none ${
                    item.isOnline
                      ? "text-emerald-600 dark:text-emerald-300"
                      : item.isUnknown
                      ? "text-slate-500 dark:text-slate-300"
                      : "text-red-500 dark:text-red-300"
                  }`}
                >
                  {item.isOnline ? "ONLINE" : item.isUnknown ? "확인 중" : "OFFLINE"}
                </div>
              </div>

              <div className="min-w-0 rounded-md bg-slate-50 px-1.5 py-1 dark:bg-white/5">
                <div className="text-[10px] font-medium leading-none text-gray-400 dark:text-gray-500">
                  배터리
                </div>

                <div className="mt-1 truncate text-[11px] font-semibold leading-none text-gray-800 dark:text-gray-100">
                  {item.battery !== null && item.battery !== undefined
                    ? `${item.battery}%`
                    : "-"}
                </div>
              </div>
            </div>
          </div>
        ))}

        {previewItems.length < MAX_PREVIEW_CAMERA_COUNT &&
          Array.from({
            length: MAX_PREVIEW_CAMERA_COUNT - previewItems.length,
          }).map((_, idx) => (
            <div
              key={`empty-${idx}`}
              className="min-h-0 rounded-[12px] border border-dashed border-blue-100 bg-white/40 dark:border-blue-500/10 dark:bg-black/5"
            />
          ))}
      </div>
    </div>
  );
}